import { customElement } from "lit/decorators.js";
import { SugarElement } from "../configurator/SugarCanvas";
import { Engine, Entity, registerEvents } from "../engine/Engine";
import { SmoothOrbitController } from "../configurator/SmoothOrbitController";
import { Vector3 } from "three";
import { logger } from "../util/Logger";

@registerEvents("pointerdown")
class CameraAnimationEntity extends Entity {
  private _sceneStartInstance: any;
  private _animationInterval?: number;
  private _animationDirection = 1; // 1: sağa, -1: sola
  private _animationSpeed = 5; // Hareket hızı (birim/saniye)
  private _maxDistance = 2.0; // Maksimum hareket mesafesi
  private _startPosition?: Vector3;
  private _currentDistance = 0;

  private _directionChangedCounter = 0;
  constructor() {
    super();
    this._sceneStartInstance = this.sceneStart.bind(this);
  }

  private getSmoothOrbitController(): SmoothOrbitController | null {
    if (!this.engine) return null;

    // Engine'deki entities array'inde SmoothOrbitController'ı bul
    const controller = this.engine.entities.find(
      (entity) => entity instanceof SmoothOrbitController
    ) as SmoothOrbitController | undefined;

    return controller || null;
  }

  private animateCamera(deltaTime: number) {
    if (this._stopped) {
      this._stopped = false;
      return;
    }
    const controller = this.getSmoothOrbitController();
    if (!controller || !this.engine?.activeCamera) return;

    // OrbitControls üzerinden kameraya eriş
    const orbitControls = this.engine.activeCamera.controller;
    if (!orbitControls) return;

    const camera = orbitControls.object;
    const currentPosition = camera.position.clone();
    const currentTarget = orbitControls.target.clone();

    // Başlangıç pozisyonunu kaydet
    if (!this._startPosition) {
      this._startPosition = currentPosition.clone();
      this._currentDistance = 0;
    }

    // Kameranın sağa/sola vektörünü hesapla
    const forward = new Vector3()
      .subVectors(currentTarget, currentPosition)
      .normalize();
    const right = new Vector3().crossVectors(forward, camera.up).normalize();

    // Hareket miktarı (deltaTime ile çarpıyoruz ki frame rate'den bağımsız olsun)
    const moveAmount =
      this._animationDirection * this._animationSpeed * deltaTime;
    const newPosition = currentPosition
      .clone()
      .add(right.multiplyScalar(moveAmount));

    // Mesafe hesapla
    const distanceFromStart = newPosition.distanceTo(this._startPosition);

    // Maksimum mesafeye ulaşıldıysa yön değiştir
    if (distanceFromStart >= this._maxDistance) {
      this._animationDirection *= -1;
      this._startPosition = currentPosition.clone(); // Yeni başlangıç pozisyonu
      this._directionChangedCounter++;
    }

    // Yeni pozisyonu ayarla (smooth animasyon için force=false)
    controller.setOrbitPosition(newPosition, false);
  }

  private startCameraAnimation() {
    // Mevcut animasyonu durdur
    this.stopCameraAnimation();

    // Başlangıç pozisyonunu sıfırla
    this._startPosition = undefined;
    this._currentDistance = 0;

    // Yeni animasyon başlat (her frame'de çağrılacak)
    let lastTime = performance.now();

    const animate = (currentTime: number) => {
      if (this._stopped) {
        this._stopped = false;
        return;
      }
      if (!this.engine) return;

      const deltaTime = (currentTime - lastTime) / 1000; // saniye cinsinden
      lastTime = currentTime;

      // Animasyonu çalıştır (deltaTime ile)
      this.animateCamera(deltaTime);

      if (this._directionChangedCounter < 2) {
        this._animationInterval = requestAnimationFrame(animate) as any;
      }
    };

    this._animationInterval = requestAnimationFrame(animate) as any;
  }

  private _stopped = false;

  private stopCameraAnimation() {
    if (this._animationInterval !== undefined) {
      cancelAnimationFrame(this._animationInterval);
      this._animationInterval = undefined;
    }
  }

  onPointerdownOccured(event: MouseEvent) {
    this._stopped = true;
    this.getSmoothOrbitController()?.cancelAnimation();
    this.stopCameraAnimation();
  }

  private sceneStart(e: any) {

    // SmoothOrbitController'ı bekle (biraz gecikme ile)
    setTimeout(() => {
      const controller = this.getSmoothOrbitController();
      if (controller) {
        logger.info("SmoothOrbitController bulundu, animasyon başlatılıyor");
        this.startCameraAnimation();
      } else {
        logger.warn("SmoothOrbitController bulunamadı");
      }
    }, 100);
  }

  onAdd(engine: Engine): void {
    engine.addEventListener("scene-first-updated", this._sceneStartInstance);
  }

  onRemove(engine: Engine): void {
    engine.removeEventListener("scene-first-updated", this._sceneStartInstance);
    this.stopCameraAnimation();
  }
}

@customElement("camera-animation")
export class CameraAnimationElement extends SugarElement {
  createEntity(): Entity | undefined {
    return new CameraAnimationEntity();
  }
}
